import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCountry } from './CountryContext';
import { useWishlist } from './WishlistContext';
import { useEventTracking } from '../hooks/useAnalytics';
import { useSellerAnalytics } from '../hooks/useSellerAnalytics';

const ProductCard = ({ product, view = 'grid', shippingRate }) => {
  const { selectedCountry } = useCountry();
  const { wishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { trackProductClick, trackExternalClick, trackWishlistAdd, trackWishlistRemove } = useEventTracking();
  const { trackProductClick: trackBusinessClick, trackExternalClick: trackBusinessExternalClick, trackWishlistAdd: trackBusinessWishlistAdd, observeProduct } = useSellerAnalytics();
  const analyticsRef = useRef(null); // Ref separato per analytics
  const isInWishlist = wishlist.includes(product.id);

  // Osserva il prodotto per tracciare impression (usando ref separato)
  useEffect(() => {
    if (analyticsRef.current) {
      observeProduct(analyticsRef.current, product);
    }
  }, [observeProduct, product]);

  // Calcola lo sconto solo se compare_at_price_min > price_min
  const calculateDiscount = () => {
    if (
      product.compare_at_price_min &&
      product.price_min &&
      parseFloat(product.compare_at_price_min) > parseFloat(product.price_min)
    ) {
      const discount = ((product.compare_at_price_min - product.price_min) / product.compare_at_price_min) * 100;
      return Math.round(discount);
    }
    return null;
  };

  const discount = calculateDiscount();
  const showDiscount = discount && discount > 0;

  // Mostra il prezzo barrato solo se compare_at_price_min > price_min
  const showCompareAtPrice =
    product.compare_at_price_min &&
    parseFloat(product.compare_at_price_min) > parseFloat(product.price_min);

  // Mostra il range di prezzo solo se price_min !== price_max
  const showPriceRange =
    product.price_min !== undefined &&
    product.price_max !== undefined &&
    parseFloat(product.price_min) !== parseFloat(product.price_max);

  // Mostra il range barrato solo se compare_at_price_min e compare_at_price_max sono diversi e > price_min/price_max
  const showCompareAtPriceRange =
    product.compare_at_price_min &&
    product.compare_at_price_max &&
    (parseFloat(product.compare_at_price_min) !== parseFloat(product.compare_at_price_max)) &&
    (parseFloat(product.compare_at_price_min) > parseFloat(product.price_min) ||
      parseFloat(product.compare_at_price_max) > parseFloat(product.price_max));

  // Helper per aggiungere parametri UTM
  const addUtmParams = (url) => {
    if (!url) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}utm_source=loverlist&utm_medium=product_card&utm_campaign=acquista_ora`;
  };

  // Handler per click su prodotto (Scopri)
  const handleProductClick = () => {
    // Google Analytics
    trackProductClick({
      id: product.id,
      name: product.title,
      category: product.product_type,
      price: parseFloat(product.price_min),
      sellerId: product.vendor_handle
    });
    
    // Business Analytics
    trackBusinessClick(product);
  };

  // Handler per click su link esterno (Acquista ora)
  const handleExternalClick = () => {
    // Google Analytics
    trackExternalClick({
      id: product.id,
      name: product.title,
      category: product.product_type,
      price: parseFloat(product.price_min),
      sellerId: product.vendor_handle
    }, {
      id: product.vendor_handle,
      name: product.vendor
    });
    
    // Business Analytics
    trackBusinessExternalClick(product);
  };

  // Funzione per formattare le tariffe di spedizione
  const formatShippingInfo = () => {
    if (!shippingRate) {
      return <span className="product-card-shipping-unavailable">Caricamento spedizione...</span>;
    }

    // Se il prodotto è in loading, mostra il messaggio di caricamento
    if (shippingRate.loading) {
      return <span className="product-card-shipping-loading">Caricamento spedizione...</span>;
    }

    if (!shippingRate.has_rates) {
      return <span className="product-card-shipping-unavailable">Nessuna tariffa di spedizione disponibile</span>;
    }

    if (!shippingRate.shipping_rate) {
      return <span className="product-card-shipping-unavailable">Il venditore non spedisce in {selectedCountry}</span>;
    }

    const { shipping_cost } = shippingRate.shipping_rate;
    
    if (shipping_cost === 0) {
      return <span className="product-card-shipping-free">Spedizione gratuita</span>;
    }

    return (
      <span className="product-card-shipping-cost">
        Spedizione: €{shipping_cost.toFixed(2)}
      </span>
    );
  };

  // Pulsante cuoricino wishlist
  const handleWishlistClick = (e) => {
    e.preventDefault();
    if (isInWishlist) {
      removeFromWishlist(product.id);
      trackWishlistRemove({
        id: product.id,
        name: product.title,
        category: product.product_type,
        price: parseFloat(product.price_min),
        sellerId: product.vendor_handle
      });
    } else {
      addToWishlist(product.id);
      // Google Analytics
      trackWishlistAdd({
        id: product.id,
        name: product.title,
        category: product.product_type,
        price: parseFloat(product.price_min),
        sellerId: product.vendor_handle
      });
      // Business Analytics
      trackBusinessWishlistAdd(product);
    }
  };

  // --- IMMAGINE + CUORE ---
  const renderImageBlock = () => (
    <div className="product-card-img" style={{ position: 'relative' }}>
      <button
        className={`wishlist-btn${isInWishlist ? ' active' : ''}`}
        onClick={handleWishlistClick}
        aria-label={isInWishlist ? 'Rimuovi dalla wishlist' : 'Aggiungi alla wishlist'}
      >
        {isInWishlist ? (
          <svg width="28" height="28" viewBox="0 0 28 28" fill="#c93249" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 25s-8.5-6.7-11.3-10.1C-1.2 10.2 2.1 4 8 4c2.5 0 4.1 1.3 5 2.5C14.9 5.3 16.5 4 19 4c5.9 0 9.2 6.2 5.3 10.9C22.5 18.3 14 25 14 25z"/>
          </svg>
        ) : (
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="#c93249" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 25s-8.5-6.7-11.3-10.1C-1.2 10.2 2.1 4 8 4c2.5 0 4.1 1.3 5 2.5C14.9 5.3 16.5 4 19 4c5.9 0 9.2 6.2 5.3 10.9C22.5 18.3 14 25 14 25z"/>
          </svg>
        )}
      </button>
      <Link to={`/products/${product.id}`}>
        <img src={product.main_image_url} alt={product.title} />
      </Link>
      {showDiscount && (
        <div className="product-card-discount-badge">-{discount}%</div>
      )}
    </div>
  );

  return (
    <div ref={analyticsRef} className={`product-card ${view === 'list' ? 'product-card-list' : 'product-card-grid'}`}>
      {view === 'list' ? (
        <div className="product-card-list-inner">
          {renderImageBlock()}
          <div className="product-card-body">
            <div className="product-card-title-row">
              <h5 className="product-card-title">
                <Link to={`/products/${product.id}`}>{product.title}</Link>
              </h5>
              {showDiscount && (
                <span className="product-card-discount">-{discount}%</span>
              )}
            </div>
            <div className="product-card-meta-row">
              {product.brand && (
                <Link to={`/brands/${encodeURIComponent(product.brand)}`} className="product-card-brand">{product.brand}</Link>
              )}
            </div>
            <div className="product-card-meta-row">
            {product.vendor_handle && (
                <Link to={`/seller/${product.vendor_handle}`} className="product-card-vendor">{product.vendor}</Link>
            )}
            </div>
            <div className="product-card-pricing-row">
              {showPriceRange ? (
                <span className="product-card-price">
                  €{parseFloat(product.price_min).toFixed(2)} - €{parseFloat(product.price_max).toFixed(2)}
                </span>
              ) : (
                <span className="product-card-price">
                  €{parseFloat(product.price_min).toFixed(2)}
                </span>
              )}
              {showCompareAtPrice && !showCompareAtPriceRange && (
                <span className="product-card-compare-price">
                  €{parseFloat(product.compare_at_price_min).toFixed(2)}
                </span>
              )}
              {showCompareAtPriceRange && (
                <span className="product-card-compare-price">
                  €{parseFloat(product.compare_at_price_min).toFixed(2)} - €{parseFloat(product.compare_at_price_max).toFixed(2)}
                </span>
              )}
            </div>

            {/* Informazioni spedizione */}
            <div className="product-card-shipping">
              {formatShippingInfo()}
            </div>

            <div className="product-card-actions">
              <a 
                href={addUtmParams(product.main_product_url)} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="product-card-buy-btn"
                onClick={handleExternalClick}
              >
                Acquista ora
              </a>
              <Link 
                to={`/products/${product.id}`} 
                className="product-card-discover-btn"
                onClick={handleProductClick}
              >
                Scopri
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="product-card-grid-inner">
          {renderImageBlock()}
          <div className="product-card-body">
            <h5 className="product-card-title">
              <Link to={`/products/${product.id}`}>{product.title}</Link>
            </h5>
            {product.brand && (
                <Link className="product-card-brand" to={`/brands/${encodeURIComponent(product.brand)}`}>{product.brand}</Link>
            )}
            {product.vendor_handle && (
                <Link className="product-card-vendor" to={`/seller/${product.vendor_handle}`}>{product.vendor}</Link>
            )}
            <div className="product-card-pricing">
              {showPriceRange ? (
                <span className="product-card-price">
                  €{parseFloat(product.price_min).toFixed(2)} - €{parseFloat(product.price_max).toFixed(2)}
                </span>
              ) : (
                <span className="product-card-price">
                  €{parseFloat(product.price_min).toFixed(2)}
                </span>
              )}
              {showCompareAtPrice && !showCompareAtPriceRange && (
                <span className="product-card-compare-price">
                  €{parseFloat(product.compare_at_price_min).toFixed(2)}
                </span>
              )}
              {showCompareAtPriceRange && (
                <span className="product-card-compare-price">
                  €{parseFloat(product.compare_at_price_min).toFixed(2)} - €{parseFloat(product.compare_at_price_max).toFixed(2)}
                </span>
              )}
            </div>

            {/* Informazioni spedizione */}
            <div className="product-card-shipping">
              {formatShippingInfo()}
            </div>
            
            <div className="product-card-actions">
              <a 
                href={addUtmParams(product.main_product_url)} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="product-card-buy-btn"
                onClick={handleExternalClick}
              >
                Acquista ora
              </a>
              <Link 
                to={`/products/${product.id}`} 
                className="product-card-discover-btn"
                onClick={handleProductClick}
              >
                Scopri
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCard;